import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { API_BASE_URL } from "@/services/api";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalizes a media URL (resume, logo, etc.) to ensure it points to the correct Gateway endpoint.
 * Handles relative paths, absolute paths with internal Docker hostnames, and already correct URLs.
 */
export function getMediaUrl(url: string | null | undefined): string {
  if (!url) return "";

  // If it's already a full URL pointing to our API, leave it
  if (url.startsWith(API_BASE_URL)) {
    return url;
  }

  // If it's already an absolute URL but NOT pointing to our API (e.g. external image)
  if (url.startsWith("http://") || url.startsWith("https://")) {
    // Check if it's an internal Docker URL (e.g. http://user-service:9001/api/...)
    // replace the internal base with the public API_BASE_URL
    if (url.includes("user-service") || url.includes(":9001") || url.includes("employer-service") || url.includes(":9006")) {
      try {
        const urlObj = new URL(url);
        const path = urlObj.pathname;
        const baseUrl = API_BASE_URL.endsWith("/api") 
          ? API_BASE_URL.substring(0, API_BASE_URL.length - 4) 
          : API_BASE_URL;
        return `${baseUrl}${path}`;
      } catch (e) {
        console.warn("Failed to parse internal URL, returning as is:", url);
        return url;
      }
    }
    return url;
  }

  // If it's a relative path starting with /api
  if (url.startsWith("/api")) {
    // API_BASE_URL usually ends with /api, so we need to be careful not to double it
    const baseUrl = API_BASE_URL.endsWith("/api") 
      ? API_BASE_URL.substring(0, API_BASE_URL.length - 4) 
      : API_BASE_URL;
    return `${baseUrl}${url}`;
  }

  // If it's a relative path starting with /uploads (old logo format)
  if (url.startsWith("/uploads")) {
    const baseUrl = API_BASE_URL.endsWith("/api") 
      ? API_BASE_URL.substring(0, API_BASE_URL.length - 4) 
      : API_BASE_URL;
    return `${baseUrl}/api/employers${url}`;
  }

  // If it's just a filename (no slash)
  if (!url.includes("/")) {
    return `${API_BASE_URL}/users/resume/${url}`;
  }

  return url;
}

// Keep getResumeUrl as an alias for backward compatibility
export const getResumeUrl = getMediaUrl;
